import { computed } from 'vue'
import { useToast } from '@/composables/useToast'
import { refreshTonWalletUser, tonWalletBindingState } from '../bindings'
import { getTonWalletController } from '../controller'
import { projectWalletPresentationState } from './presentation'

export function useWallet() {
  const wallet = getTonWalletController()
  const { error: showError } = useToast()
  const isConnected = computed(() => wallet.selectors.isConnected.value)
  const isAuthenticated = computed(() => wallet.selectors.isAuthenticated.value)
  const hasActiveTonConnectSession = computed(() => wallet.selectors.hasActiveSession.value)
  const isTonConnectRestoreSettled = computed(() => wallet.selectors.isRestoreSettled.value)
  const isBackendAuthRestoring = computed(() => wallet.selectors.isBackendAuthRestoring.value)
  const isConnecting = computed(() => wallet.selectors.isConnecting.value)
  const address = computed(() => wallet.selectors.address.value)
  const shortAddress = computed(() => wallet.selectors.shortAddress.value)
  const isSessionValid = computed(() => wallet.selectors.hasRestorableSession.value)
  const walletPresentationState = computed(() =>
    projectWalletPresentationState({
      isAuthenticated: isAuthenticated.value,
      isTonConnectRestoreSettled: isTonConnectRestoreSettled.value,
      isBackendAuthRestoring: isBackendAuthRestoring.value,
    })
  )

  const refreshAfterSuccessfulConnection = async (
    command: () => Promise<boolean>
  ): Promise<boolean> => {
    const connected = await command()
    if (connected) await refreshTonWalletUser()
    return connected
  }

  const connect = async (): Promise<boolean> => {
    if (isConnected.value) return true
    if (hasActiveTonConnectSession.value) {
      let restored: boolean
      try {
        restored = await wallet.commands.recheckBackendSession()
      } catch (error) {
        showError('Unable to verify your wallet session. Please try again.')
        throw error
      }
      if (restored) {
        await refreshTonWalletUser()
        return true
      }
      return refreshAfterSuccessfulConnection(wallet.commands.reconnectForFreshProof)
    }
    return refreshAfterSuccessfulConnection(wallet.commands.connect)
  }

  const reconnectForFreshProof = async (): Promise<boolean> => {
    return refreshAfterSuccessfulConnection(wallet.commands.reconnectForFreshProof)
  }

  const recheckBackendSession = (): Promise<boolean> => wallet.commands.recheckBackendSession()

  return {
    // State
    isConnected,
    isAuthenticated,
    hasActiveTonConnectSession,
    isTonConnectRestoreSettled,
    isBackendAuthRestoring,
    walletPresentationState,
    isConnecting,
    address,
    shortAddress,
    user: computed(() => wallet.selectors.user.value),

    isLoadingUser: tonWalletBindingState.isLoadingUser,
    isSessionValid,
    connect,
    recheckBackendSession,
    reconnectForFreshProof,
    disconnect: wallet.commands.disconnect,
    openExplorer: wallet.commands.openExplorer,
    openAddress: wallet.commands.openAddress,
    refetchUser: refreshTonWalletUser,
  }
}
